/* eslint-disable no-unused-vars */
import React, {
  useState, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { useProject } from '../../config/ProjectContext';
import { ChevronDownIcon, StarIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';

export default function ProjectPicker({ projects, mode = 'single', onDone }) {
  const { project, setProject, selectedProjects: globalSelectedProjects, setSelectedProjects } = useProject();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const pickerRef = useRef(null);
  const [pinnedProjectIds, setPinnedProjectIds] = useState([]);
  const [selectedProjects, setLocalSelectedProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [error, setError] = useState('');

  // Determine if multiSelect mode
  const multiSelect = mode === 'multiSelect';
  // Determine if readOnly mode (show selected project as label, no interaction)
  const readOnly = mode === 'readOnly';

  useEffect(() => {
    const storedPinnedProjects = localStorage.getItem('pinnedProjects');
    if (storedPinnedProjects) {
      try {
        setPinnedProjectIds(JSON.parse(storedPinnedProjects));
      } catch (e) {
        console.error("Failed to parse pinned projects from localStorage", e);
        setPinnedProjectIds([]);
      }
    }
    if (multiSelect) {
      setLocalSelectedProjects(globalSelectedProjects || []);
    } else {
      setSelectedProject(project);
    }
  }, [mode, globalSelectedProjects, project]);

  useEffect(() => {
    if (pinnedProjectIds.length > 0) {
      localStorage.setItem('pinnedProjects', JSON.stringify(pinnedProjectIds));
    }
  }, [pinnedProjectIds]);

  // Eliminado: la persistencia se hace solo al pulsar Done

  const handleButtonClick = () => {
    if (readOnly) {
      // Don't allow opening modal in readOnly mode
      return;
    }
    setIsModalOpen(true);
    setSearchTerm('');
  };

  const closeModal = () => {
    if (multiSelect && (!selectedProjects || selectedProjects.length === 0)) {
      setError('You must select at least one project.');
      return;
    }
    setError('');
    setIsModalOpen(false);

    // Solo en multiselect aplicar los cambios al contexto global
    if (multiSelect) {
      localStorage.setItem('selectedProjects', JSON.stringify(selectedProjects));
      setSelectedProjects(selectedProjects);
      if (selectedProjects && selectedProjects.length > 0) {
        localStorage.setItem('project', JSON.stringify(selectedProjects[0]));
        setProject(selectedProjects[0]);
      }
      if (typeof onDone === 'function') {
        onDone(selectedProjects);
      }
    }

    window.dispatchEvent(new CustomEvent('projectPickerDone'));
  };

  const forceCloseModal = () => {
    setError('');
    setIsModalOpen(false);
    // En modo multiselect, al cerrar con la cruz restaurar el estado original
    if (multiSelect) {
      setLocalSelectedProjects(globalSelectedProjects || []);
    }
    // En modo single select, no aplicar cambios al cerrar con la cruz
  };

  const handleItemClick = async (item) => {
    if (multiSelect) {
      // Solo actualizar el estado local, no aplicar cambios hasta Done
      setLocalSelectedProjects((prev) => {
        const exists = prev.find((p) => p.id === item.id);
        if (exists) {
          return prev.filter((p) => p.id !== item.id);
        }
        return [...prev, item];
      });
    } else {
      // En single select, aplicar cambios inmediatamente
      setProject(item);
      localStorage.setItem('project', JSON.stringify(item));
      setIsModalOpen(false);
      setError('');
      if (typeof onDone === 'function') {
        onDone([item]);
      }
      window.dispatchEvent(new CustomEvent('projectPickerDone'));
    }
  };

  // Eliminado: no re-inicializar project después del primer render

  const isOnlyL3SupportProject = projects && projects.length === 1
    && projects[0].role.includes('l3Support');

  useEffect(() => {
    if (isOnlyL3SupportProject) {
      setProject(projects[0]);
      localStorage.setItem('project', JSON.stringify(projects[0]));
    }
  }, [isOnlyL3SupportProject, projects, setProject]);

  useEffect(() => {
    if (multiSelect) return; // No cerrar modal al hacer click fuera en multiselect
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsModalOpen(false);
      }
    };
    if (isModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModalOpen, multiSelect]);

  const isLoading = projects === undefined;
  const hasNoProjects = projects !== undefined && projects.length === 0;
  const isDisabled = isLoading || hasNoProjects;



  const filteredProjects = projects && projects.filter(proj =>
    proj.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const togglePinProject = (projectId, event) => {
    event.stopPropagation(); // Prevent modal from closing or project selection
    setPinnedProjectIds(prevPinnedIds => {
      if (prevPinnedIds.includes(projectId)) {
        return prevPinnedIds.filter(id => id !== projectId);
      }
      return [...prevPinnedIds, projectId];
    });
  };

  const getSortedProjects = () => {
    if (!filteredProjects) return [];
    const pinned = [];
    const unpinned = [];
    filteredProjects.forEach(p => {
      if (pinnedProjectIds.includes(p.id)) {
        pinned.push(p);
      } else {
        unpinned.push(p);
      }
    });
    return [...pinned, ...unpinned];
  };

  const sortedProjects = getSortedProjects();

  const getDisplayText = () => {
    if (multiSelect) {
      // Multi-select mode: show selected projects or placeholder
      if (selectedProjects.length > 0) {
        return selectedProjects.map(p => p.name).join(', ');
      }
      if (isLoading) return 'Loading projects...';
      if (hasNoProjects) return 'No projects available';
      return 'Select projects';
    } else {
      // Single-select mode: show current project or placeholder
      if (project?.name) {
        return project.name;
      }
      if (isLoading) return 'Loading projects...';
      if (hasNoProjects) return 'No projects available';
      return 'Select a project';
    }
  };

  if (isOnlyL3SupportProject) {
    return null;
  }

  return (
    <div className="relative w-full max-w-md" id="project-picker-form">
      <button
        type="button"
        onClick={handleButtonClick}
        disabled={isDisabled || readOnly}
        className={`flex items-center justify-between w-full px-4 py-2 text-md text-left text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors duration-150 ${readOnly ? 'cursor-default' : ''}`}
        id="project-picker-button"
      >
        <span className="truncate text-md">
          {getDisplayText()}
        </span>
        {!readOnly && <ChevronDownIcon className="w-5 h-5 ml-2 text-gray-400" aria-hidden="true" />}
      </button>

      {isModalOpen && !isDisabled && !readOnly && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center" id="project-picker-modal-backdrop">
          <motion.div
            ref={pickerRef}
            initial={{ opacity: 0, scale: 0.90 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.90 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="relative bg-white border border-gray-300 rounded-lg shadow-xl max-h-[80vh] w-11/12 md:w-1/2 lg:w-1/3 flex flex-col"
            id="project-picker-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-md  font-semibold text-gray-800">{multiSelect ? 'Select Projects' : 'Select Project'}</h3>
              <button onClick={forceCloseModal} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor"><path d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>

            <div className="p-4 border-b">
              <input
                type="text"
                placeholder="Search projects..."
                className="w-full px-4 py-3 text-md border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {multiSelect && (
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setLocalSelectedProjects(sortedProjects)}
                    className="flex items-center gap-1.5 px-3 py-1 text-sm bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition-colors duration-150"
                  >
                    <CheckIcon className="w-4 h-4" />
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => setLocalSelectedProjects([])}
                    className="flex items-center gap-1.5 px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors duration-150"
                  >
                    <XMarkIcon className="w-4 h-4" />
                    Unselect All
                  </button>
                </div>
              )}
              {multiSelect && error && (
                <div className="mt-2 text-red-600 text-sm">{error}</div>
              )}
            </div>
            <div className="py-2 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 flex-grow">
              {sortedProjects && sortedProjects.length > 0 ? (
                (() => {
                  let separatorRendered = false;
                  const pinnedCount = sortedProjects.filter(p => pinnedProjectIds.includes(p.id)).length;

                  return sortedProjects.map((item, index) => {
                    const isPinned = pinnedProjectIds.includes(item.id);
                    let separator = null;

                    if (!isPinned && !separatorRendered && pinnedCount > 0 && index >= pinnedCount) {
                      separator = <hr key="separator" className="my-2 border-gray-300" />;
                      separatorRendered = true;
                    }

                    // NUEVO: checkbox si multiSelect
                    return (
                      <React.Fragment key={item.id}>
                        {separator}
                        <div className="flex items-center justify-between hover:bg-indigo-600 hover:text-white group">
                          <button
                            type="button"
                            onClick={() => handleItemClick(item)}
                            className="block w-full px-6 py-4 text-md text-left text-gray-700 group-hover:text-white focus:outline-none focus:bg-indigo-600 focus:text-white transition-colors duration-150 flex items-center"
                            role="menuitem"
                          >
                            {multiSelect && (
                              <input
                                type="checkbox"
                                checked={!!selectedProjects.find(p => p.id === item.id)}
                                readOnly
                                className="mr-2 accent-indigo-600"
                              />
                            )}
                            {item.name}
                          </button>
                          <button
                            onClick={(e) => togglePinProject(item.id, e)}
                            className="p-2 mr-4 rounded-full hover:bg-indigo-700 group-hover:text-white focus:outline-none"
                            aria-label={isPinned ? 'Unpin project' : 'Pin project'}
                          >
                            {isPinned ? (
                              <StarIcon className="w-5 h-5 text-indigo-600 group-hover:text-white" />
                            ) : (
                              <StarOutlineIcon className="w-5 h-5 text-gray-400 group-hover:text-white" />
                            )}
                          </button>
                        </div>
                      </React.Fragment>
                    );
                  });
                })()
              ) : (
                <div className="px-6 py-4 text-lg text-gray-500">{searchTerm ? 'No projects match your search.' : 'No projects found.'}</div>
              )}
            </div>
            {/* NUEVO: botón de cerrar para multiselect */}
            {multiSelect && (
              <div className="flex justify-end p-4 border-t">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                  Done
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}

ProjectPicker.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    role: PropTypes.arrayOf(PropTypes.string),
  })),
  mode: PropTypes.oneOf(['single', 'multiSelect', 'readOnly']),
  onDone: PropTypes.func,
};

ProjectPicker.defaultProps = {
  // projects stays undefined while loading so the "Loading projects..." state is reachable.
  projects: undefined,
  mode: 'single',
  onDone: undefined,
};
